// src/common/pages/dniUpload/guiaEnVivo/useGuiaEnVivo.ts
/**
 * La guía en vivo de la cámara, conectada al video: unos 5 cuadros por segundo, cada uno
 * achicado a ~320 px (analizarCuadro) más la banda de texto a resolución alta para la
 * nitidez; el estado lo lleva GuiaEnVivo. Cuando pide leer la banda, se toma la banda a
 * resolución alta y se la pasa al OCR ya cargado; si se lee, la foto se saca sola en el
 * cuadro siguiente, si sigue verde, por el mismo camino que el botón (`onCapturar`), con una
 * vibración corta.
 *
 * Si el equipo no da —sin canvas, o cuadros que tardan demasiado en analizarse, varios
 * seguidos— la guía se apaga y queda el marco estático hasta que se vuelva a abrir la cámara. Si el OCR no carga, la guía sigue guiando pero no saca
 * la foto sola: queda el botón.
 *
 * Las dependencias con el navegador (tomar cuadros, el OCR, el reloj, la vibración) se
 * pueden reemplazar: así se prueba con un video simulado.
 *
 * Con el marco girado del celular (encuadre.ts, KAN-464), los cuadros y las bandas se toman del
 * video «derecho» —girado para que la tarjeta quede apaisada—: el análisis, las esquinas, la banda
 * y su lectura son los mismos, y con las mismas medidas, que con el celular horizontal.
 */
import { useEffect, useRef, useState } from "react";
import { marcoDeLaTarjeta } from "../reader/marcoDeLaTarjeta";
import {
  dibujarDerecho,
  giroDelMarco,
  mediaVueltaDe,
  medidasDerechas,
} from "../encuadre";
import type { GiroDelMarco } from "../encuadre";
import { loadTesseractEngine } from "../tesseractLoader";
import {
  aGrises,
  analizarCuadro,
  nitidezDeLaBanda,
  UMBRALES_DE_LA_GUIA,
} from "./analizarCuadro";
import type { CuadroGris } from "./analizarCuadro";
import {
  bandaEnElCuadro,
  leeLaBanda,
  LISTA_DE_LA_BANDA,
} from "./bandaDeLectura";
import type { CaraDeLaGuia } from "./bandaDeLectura";
import { GuiaEnVivo } from "./guia";
import type { EstadoDeLaGuia } from "./guia";

/** El ancho del cuadro que se analiza. */
const ANCHO_DEL_CUADRO = 320;
/** El ancho máximo de la banda en la que se mide la nitidez. */
const ANCHO_DE_LA_BANDA = 640;
/** Cada cuánto se analiza un cuadro, en ms (~5 por segundo). */
const ENTRE_CUADROS = 200;
/** Si analizar un cuadro tarda más que esto, cuadro tras cuadro, el equipo no da: marco estático. */
const COSTO_MAXIMO_MS = 120;
/** Cuántos cuadros seguidos lentos (o sin imagen) apagan la guía: uno pasajero no. */
const CUADROS_PARA_APAGAR = 5;

export interface CuadroDelVideo {
  general: CuadroGris;
  banda: CuadroGris;
  /**
   * Con el marco girado, la misma banda de una tarjeta cabeza abajo (el celular sostenido al
   * revés), dada vuelta: el sentido no se sabe y la nitidez se mide en las dos.
   */
  bandaInvertida?: CuadroGris;
}

export interface DependenciasDeLaGuia {
  /**
   * El cuadro chico y la banda de texto, en grises y con la tarjeta apaisada (girados si el marco
   * va girado); null si no se puede (sin canvas).
   */
  tomarCuadro(
    video: HTMLVideoElement,
    cara: CaraDeLaGuia,
    giro: GiroDelMarco,
  ): CuadroDelVideo | null;
  /**
   * La banda fija a resolución alta (ampliada si el video es chico), derecha y lista para el OCR.
   * Con `invertida`, la de una tarjeta cabeza abajo, dada vuelta.
   */
  bandaParaLeer(
    video: HTMLVideoElement,
    cara: CaraDeLaGuia,
    giro: GiroDelMarco,
    invertida?: boolean,
  ): HTMLCanvasElement | null;
  /** El OCR de la banda: el texto leído. */
  leer(banda: HTMLCanvasElement, cara: CaraDeLaGuia): Promise<string>;
  ahora(): number;
  /** Llama a `cuadro` en cada cuadro del video, no más de una vez cada ENTRE_CUADROS ms. Devuelve cómo parar. */
  programar(video: HTMLVideoElement, cuadro: () => void): () => void;
  vibrar(ms: number): void;
}

interface Lienzo {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
}

/**
 * Un lienzo que se reusa cuadro a cuadro (revisión, menor 4): crear dos canvas por cuadro, unos
 * diez por segundo, carga al recolector de basura en un celular. Solo cambia de tamaño si hace falta.
 */
function lienzoReusable(): (ancho: number, alto: number) => Lienzo | null {
  let l: Lienzo | null | undefined;
  return (ancho, alto) => {
    if (l === undefined) {
      const canvas = document.createElement("canvas");
      let ctx: CanvasRenderingContext2D | null = null;
      try {
        ctx = canvas.getContext("2d", {
          willReadFrequently: true,
        }) as CanvasRenderingContext2D | null;
      } catch {
        ctx = null;
      }
      l = ctx ? { canvas, ctx } : null;
    }
    if (!l) return null;
    const w = Math.max(1, Math.round(ancho));
    const h = Math.max(1, Math.round(alto));
    if (l.canvas.width !== w) l.canvas.width = w;
    if (l.canvas.height !== h) l.canvas.height = h;
    return l;
  };
}

function grisesDe(
  ctx: CanvasRenderingContext2D,
  ancho: number,
  alto: number,
): CuadroGris {
  const { data } = ctx.getImageData(0, 0, ancho, alto);
  return aGrises(data, ancho, alto);
}

/** Lo que la guía toma del navegador, con sus propios lienzos (uno por uso, reusados). */
export function dependenciasDelNavegador(): DependenciasDeLaGuia {
  const lienzoGeneral = lienzoReusable();
  const lienzoBanda = lienzoReusable();
  return {
    tomarCuadro(video, cara, giro) {
      if (!video.videoWidth || !video.videoHeight) return null;
      // Las medidas del video derecho: con el marco girado, el cuadro se toma girado.
      const { ancho: vw, alto: vh } = medidasDerechas(
        video.videoWidth,
        video.videoHeight,
        giro,
      );
      const alto = Math.round((ANCHO_DEL_CUADRO * vh) / vw);
      const g = lienzoGeneral(ANCHO_DEL_CUADRO, alto);
      if (!g) return null;
      dibujarDerecho(
        g.ctx,
        video,
        video.videoWidth,
        { x: 0, y: 0, w: vw, h: vh },
        { ancho: ANCHO_DEL_CUADRO, alto },
        giro,
      );
      const general = grisesDe(g.ctx, ANCHO_DEL_CUADRO, alto);
      const b = bandaEnElCuadro(marcoDeLaTarjeta(vw, vh), cara);
      const bw = Math.min(ANCHO_DE_LA_BANDA, b.w);
      const bh = Math.max(1, Math.round((b.h * bw) / b.w));
      const l = lienzoBanda(bw, bh);
      if (!l) return null;
      dibujarDerecho(
        l.ctx,
        video,
        video.videoWidth,
        b,
        { ancho: l.canvas.width, alto: l.canvas.height },
        giro,
      );
      const banda = grisesDe(l.ctx, l.canvas.width, l.canvas.height);
      if (giro === 0) return { general, banda };
      // El mismo lienzo, ya leído: la banda de la tarjeta cabeza abajo.
      dibujarDerecho(
        l.ctx,
        video,
        video.videoWidth,
        mediaVueltaDe(b, vw, vh),
        { ancho: l.canvas.width, alto: l.canvas.height },
        giro,
        true,
      );
      return {
        general,
        banda,
        bandaInvertida: grisesDe(l.ctx, l.canvas.width, l.canvas.height),
      };
    },
    bandaParaLeer(video, cara, giro, invertida = false) {
      if (!video.videoWidth || !video.videoHeight) return null;
      const { ancho: vw, alto: vh } = medidasDerechas(
        video.videoWidth,
        video.videoHeight,
        giro,
      );
      const marco = marcoDeLaTarjeta(vw, vh);
      const derecha = bandaEnElCuadro(marco, cara);
      const b = invertida ? mediaVueltaDe(derecha, vw, vh) : derecha;
      // Ampliada si el video es chico: con la tarjeta de unos 1000 px de alto, las letras
      // del encabezado y del MRZ miden lo que el OCR lee bien.
      const f = Math.max(1, Math.min(3, 1000 / marco.h));
      // Este no se reusa: va al OCR, que lo lee mientras siguen llegando cuadros (una vez por segundo).
      const l = lienzoReusable()(b.w * f, b.h * f);
      if (!l) return null;
      dibujarDerecho(
        l.ctx,
        video,
        video.videoWidth,
        b,
        { ancho: l.canvas.width, alto: l.canvas.height },
        giro,
        invertida,
      );
      return l.canvas;
    },
    async leer(banda, cara) {
      const motor = await loadTesseractEngine();
      return (
        await motor.recognize(banda, {
          whitelist: LISTA_DE_LA_BANDA[cara],
          psm: "6",
        })
      ).text;
    },
    ahora: () => performance.now(),
    programar(video, cuadro) {
      const v = video as HTMLVideoElement & {
        requestVideoFrameCallback?: (cb: (ahora: number) => void) => number;
        cancelVideoFrameCallback?: (id: number) => void;
      };
      if (typeof v.requestVideoFrameCallback === "function") {
        let ultimo = -Infinity;
        let id = 0;
        let parado = false;
        const cb = (ahora: number) => {
          if (parado) return;
          if (ahora - ultimo >= ENTRE_CUADROS) {
            ultimo = ahora;
            cuadro();
          }
          id = v.requestVideoFrameCallback!(cb);
        };
        id = v.requestVideoFrameCallback(cb);
        return () => {
          parado = true;
          v.cancelVideoFrameCallback?.(id);
        };
      }
      const intervalo = window.setInterval(cuadro, ENTRE_CUADROS);
      return () => window.clearInterval(intervalo);
    },
    vibrar(ms) {
      try {
        navigator.vibrate?.(ms);
      } catch {
        // Sin vibración: no pasa nada.
      }
    },
  };
}

export interface GuiaDelVideo {
  /** Si la guía corre; si no, el marco queda estático. */
  disponible: boolean;
  estado: EstadoDeLaGuia | null;
}

export function useGuiaEnVivo(opciones: {
  video: HTMLVideoElement | null;
  /** El video ya da imagen. */
  activa: boolean;
  /** Sin cara no hay guía. */
  cara?: CaraDeLaGuia;
  /** En el celular, con el video vertical, el marco va girado (encuadre.ts). */
  celular?: boolean;
  onCapturar: () => void;
  dependencias?: Partial<DependenciasDeLaGuia>;
}): GuiaDelVideo {
  const { video, activa, cara } = opciones;
  const celular = !!opciones.celular;
  const [disponible, setDisponible] = useState(true);
  const [estado, setEstado] = useState<EstadoDeLaGuia | null>(null);
  // Lo que cambia sin tener que reiniciar la guía.
  const capturar = useRef(opciones.onCapturar);
  capturar.current = opciones.onCapturar;
  const dependencias = useRef<DependenciasDeLaGuia | null>(null);
  if (!dependencias.current)
    dependencias.current = {
      ...dependenciasDelNavegador(),
      ...opciones.dependencias,
    };

  useEffect(() => {
    if (!video || !activa || !cara) return;
    const d = dependencias.current!;
    // Al (re)abrir la cámara se vuelve a probar el equipo (revisión, menor 3).
    setDisponible(true);
    setEstado(null);
    const guia = new GuiaEnVivo({
      nitidezMinima: UMBRALES_DE_LA_GUIA.nitidezMinima[cara],
      // El dorso 2009 no tiene MRZ: tras tres lecturas fallidas con todo lo demás bien, alcanza la nitidez.
      lecturasFallidasSinBanda: cara === "dorso" ? 3 : null,
    });
    let parado = false;
    let sinLectura = false;
    /**
     * Con el marco girado no se sabe hacia qué lado se giró el celular: la banda se lee en el
     * sentido supuesto y, si no se lee, en el otro, alternando. Cuando una se lee, queda esa.
     */
    let invertida = false;
    let sentidoConfirmado = false;
    let lentos = 0;
    let nulos = 0;
    let parar = () => undefined as void;
    const apagar = () => {
      parado = true;
      parar();
      setDisponible(false);
      setEstado(null);
    };
    const alCapturar = () => {
      d.vibrar(60);
      capturar.current();
    };
    const cuadro = () => {
      if (parado) return;
      const t0 = d.ahora();
      // El giro, cuadro a cuadro: si la página gira con el celular, el video cambia de forma.
      const giro = giroDelMarco(video.videoWidth, video.videoHeight, celular);
      const c = d.tomarCuadro(video, cara, giro);
      if (!c) {
        // Un cuadro sin imagen pasajero (videoWidth 0 un instante) no apaga la guía; varios seguidos, sí.
        if (++nulos >= CUADROS_PARA_APAGAR) apagar();
        return;
      }
      nulos = 0;
      const marco = marcoDeLaTarjeta(c.general.width, c.general.height);
      const a = analizarCuadro(c.general, marco);
      // Con el marco girado, la mejor de las dos bandas: la del sentido en que se sostiene el celular.
      const nitidez = c.bandaInvertida
        ? Math.max(
            nitidezDeLaBanda(c.banda),
            nitidezDeLaBanda(c.bandaInvertida),
          )
        : nitidezDeLaBanda(c.banda);
      const ahora = d.ahora();
      lentos = ahora - t0 > COSTO_MAXIMO_MS ? lentos + 1 : 0;
      if (lentos >= CUADROS_PARA_APAGAR) {
        apagar();
        return;
      }
      const r = guia.cuadro(a, nitidez, ahora);
      setEstado(r.estado);
      if (r.capturar) {
        parado = true;
        parar();
        alCapturar();
        return;
      }
      if (!r.leer || sinLectura) return;
      const enOtroSentido = giro === 90 && invertida;
      const banda = d.bandaParaLeer(video, cara, giro, enOtroSentido);
      if (!banda) {
        sinLectura = true;
        return;
      }
      d.leer(banda, cara).then(
        (texto) => {
          // Cerrada la cámara, lo leído se descarta. tesseract.js no cancela un reconocimiento en
          // curso: el worker termina esta banda (0,1 a 1 s) antes de la foto, pero la lectura de la
          // foto empieza por el código y el recorte, que llevan más que eso.
          if (parado) return;
          const leida = leeLaBanda(texto, cara);
          if (leida) sentidoConfirmado = true;
          else if (giro === 90 && !sentidoConfirmado)
            invertida = !enOtroSentido;
          setEstado(guia.lectura(leida, d.ahora()).estado);
        },
        () => {
          // Sin OCR la guía sigue guiando, pero no saca la foto sola: queda el botón.
          sinLectura = true;
          guia.lectura(false, d.ahora());
        },
      );
    };
    parar = d.programar(video, cuadro);
    return () => {
      parado = true;
      parar();
    };
  }, [video, activa, cara, celular]);

  return {
    disponible: disponible && !!cara,
    estado: disponible && cara ? estado : null,
  };
}
